import { useState, useEffect } from "react";
import { FaTrash } from "react-icons/fa";
import { TradingViewResearch } from "./TradingViewResearch";
import AgentRecommendation from "./AgentRecommendation";
import { useModalAlert } from "./ModalAlert";
import AgentKeyModal from './AgentKeyModal';
import { getAgentPrivateKey } from '../utils/privateKey';
import { useTradeGuard } from '../hooks/useTradeGuard';
import { BuilderFeeApprovalModal } from './BuilderFeeApprovalModal';
import AgentIdentityCard from './AgentIdentityCard';
import { trackEvent } from '../hooks/useGoogleAnalytics';

// Parse a UTC timestamp string that may lack a Z suffix
const parseUtcDate = (createdAt) => {
  if (!createdAt) return null;
  const s = String(createdAt);
  // If no timezone indicator, treat as UTC by appending Z
  return new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
};

// Utility function to format time ago
const formatTimeAgo = (createdAt) => {
  if (!createdAt) return '';

  const now = new Date();
  const created = parseUtcDate(createdAt);
  const diffMs = now - created;
  
  // Convert to different time units
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    // For older recommendations, show the date
    return created.toLocaleDateString();
  }
};

const TradingRecommendationForm = ({ recommendation, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    leverage_amount: recommendation?.leverage_amount || 2.0,
    take_profit_pct: recommendation?.take_profit_pct || 0,
    stop_loss_pct: recommendation?.stop_loss_pct || 0,
    recommended_position_size_usd: recommendation?.recommended_position_size_usd || 0,
    entry_price: recommendation?.entry_price || 0, // Preserve entry price - it's read-only and determined at execution
    reasoning: recommendation?.reasoning || '',
    market_context: recommendation?.market_context || ''
  });

  // Helper function to calculate take profit price
  const calculateTakeProfitPrice = () => {
    const entryPrice = parseFloat(formData.entry_price || 0);
    const tpPct = parseFloat(formData.take_profit_pct || 0);
    
    if (!entryPrice || !tpPct) return null;
    
    if (recommendation?.action === 'BUY') {
      // Long position: TP is higher than entry
      return entryPrice * (1 + tpPct / 100);
    } else {
      // Short position: TP is lower than entry  
      return entryPrice * (1 - tpPct / 100);
    }
  };

  // Helper function to calculate stop loss price
  const calculateStopLossPrice = () => {
    const entryPrice = parseFloat(formData.entry_price || 0);
    const slPct = parseFloat(formData.stop_loss_pct || 0);
    
    if (!entryPrice || !slPct) return null;
    
    if (recommendation?.action === 'BUY') {
      // Long position: SL is lower than entry
      return entryPrice * (1 - slPct / 100);
    } else {
      // Short position: SL is higher than entry
      return entryPrice * (1 + slPct / 100);
    }
  };

  const takeProfitPrice = calculateTakeProfitPrice();
  const stopLossPrice = calculateStopLossPrice();

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded overflow-hidden">
      {/* Header */}
      <div className="p-1 sm:p-4 pb-1 sm:pb-3 border-b border-slate-700/50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-0">
          <div className="flex items-center gap-1 sm:gap-3 flex-wrap">
            <span className="px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full border text-blue-400 bg-blue-400/10 border-blue-400/20">
              EDITING
            </span>
            <span className={`px-1.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full border ${
              recommendation?.action === 'BUY' 
                ? 'text-green-400 bg-green-400/10 border-green-400/20'
                : 'text-orange-400 bg-orange-400/10 border-orange-400/20'
            }`}>
              {recommendation?.action}
            </span>
            <h3 className="text-sm sm:text-lg font-semibold text-white mb-[22px]">{recommendation?.pair}</h3>
          </div>
          <div className="flex gap-1 sm:gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm rounded border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-recommendation-form"
              disabled={isSubmitting}
              className={`px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm rounded text-white ${
                isSubmitting ? 'bg-gray-600/20 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'
              }`}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-1 sm:p-4">
        <form id="edit-recommendation-form" onSubmit={handleSubmit} className="space-y-2 sm:space-y-4">
          {/* Key Metrics Grid - All fields on one row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-4">
            {/* Hidden fields - preserved for form submission */}
            <input
              type="hidden"
              name="entry_price"
              value={formData.entry_price}
            />
            <input
              type="hidden"
              name="reasoning"
              value={formData.reasoning}
            />
            <input
              type="hidden"
              name="market_context"
              value={formData.market_context}
            />
            
            <div>
              <label className="block text-xs text-slate-400 mb-2">Leverage</label>
              <select
                value={Math.round(formData.leverage_amount)}
                onChange={(e) => handleChange('leverage_amount', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-400 focus:outline-none appearance-none cursor-pointer hover:bg-slate-700/70 transition-colors [&>option]:bg-slate-800 [&>option]:text-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '16px'
                }}
              >
                <option value={1}>1x</option>
                <option value={2}>2x</option>
                <option value={5}>5x</option>
                <option value={10}>10x</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-2">Position Size (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.recommended_position_size_usd}
                onChange={(e) => handleChange('recommended_position_size_usd', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-400 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-2">Take Profit (%)</label>
              <input
                type="number"
                step="0.01"
                value={formData.take_profit_pct}
                onChange={(e) => handleChange('take_profit_pct', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-green-400 focus:border-blue-400 focus:outline-none"
              />
              {takeProfitPrice && (
                <div className="mt-1 text-xs text-green-300">
                  Price: ${takeProfitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-xs text-slate-400 mb-2">Stop Loss (%)</label>
              <input
                type="number"
                step="0.01"
                value={formData.stop_loss_pct}
                onChange={(e) => handleChange('stop_loss_pct', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-red-400 focus:border-blue-400 focus:outline-none"
              />
              {stopLossPrice && (
                <div className="mt-1 text-xs text-red-300">
                  Price: ${stopLossPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const formatRecommendationAsMarkdown = (recommendation, formatCurrency, formatPercent) => {
  const lines = [];

  // Header
  lines.push(`## ${recommendation.action} ${recommendation.pair} ${recommendation.leverage_amount}x`);
  lines.push('');

  // Key metrics
  lines.push(`**${recommendation.action} / ${recommendation.pair}**`);
  if (recommendation.leverage_amount) {
    lines.push(`**Leverage:** ${recommendation.leverage_amount}x`);
  }
  if (recommendation.confidence_pct) {
    lines.push(`**Confidence:** ${formatPercent(recommendation.confidence_pct)}`);
  }
  if (recommendation.entry_price) {
    lines.push(`**Entry Price:** ${formatCurrency(recommendation.entry_price)}`);
  }
  if (recommendation.take_profit_price) {
    lines.push(`**Take Profit:** ${formatCurrency(recommendation.take_profit_price)} (${formatPercent(recommendation.take_profit_pct)})`);
  }
  if (recommendation.stop_loss_price) {
    const slPct = parseFloat(recommendation.stop_loss_pct);
    const slSign = (recommendation.action === 'BUY') ? '-' : '+';
    lines.push(`**Stop Loss:** ${formatCurrency(recommendation.stop_loss_price)} (${slSign}${Math.abs(slPct).toFixed(2)}%)`);
  }
  if (recommendation.risk_reward_ratio) {
    lines.push(`**Risk/Reward:** ${recommendation.risk_reward_ratio.toFixed(1)}:1`);
  }
  if (recommendation.position_size_qty) {
    lines.push(`**Position Size:** ${formatCurrency(recommendation.position_size_qty)}`);
  }

  lines.push('');

  // Reasoning
  if (recommendation.reasoning) {
    lines.push('### Analysis');
    lines.push('');
    lines.push(recommendation.reasoning);
    lines.push('');
  }

  // Market Context
  if (recommendation.market_context) {
    lines.push('### Market Context');
    lines.push('');
    lines.push(recommendation.market_context);
    lines.push('');
  }

  // Technical Factors
  if (recommendation.technical_factors && recommendation.technical_factors.length > 0) {
    lines.push('### Technical Factors');
    lines.push('');
    recommendation.technical_factors.forEach(factor => {
      lines.push(`• ${factor}`);
    });
    lines.push('');
  }

  // Risk Factors
  if (recommendation.risk_factors && recommendation.risk_factors.length > 0) {
    lines.push('### Risk Factors');
    lines.push('');
    recommendation.risk_factors.forEach(factor => {
      lines.push(`• ${factor}`);
    });
    lines.push('');
  }

  // Exit Reason
  if (recommendation.exit_reason) {
    lines.push('### Exit Reason');
    lines.push('');
    lines.push(recommendation.exit_reason);
    lines.push('');
  }

  return lines.join('\n');
};

const TradingRecommendationItem = ({ recommendation, getActionColor, getConfidenceColor, deleteRecommendation, handleExecuteTradeClick, isExecuting, formatCurrency, formatPercent, formatPercentUnsigned, showWarning, onEdit, editingId, portfolio }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyMarkdown = () => {
    const markdown = formatRecommendationAsMarkdown(recommendation, formatCurrency, formatPercent);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(markdown).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Fallback for clipboard permission denied
        const el = document.createElement('textarea');
        el.value = markdown;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      // Fallback for browsers without clipboard API
      const el = document.createElement('textarea');
      el.value = markdown;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Helper function to calculate take profit price for display
  const calculateDisplayTakeProfitPrice = () => {
    const entryPrice = parseFloat(recommendation?.entry_price || 0);
    const tpPct = parseFloat(recommendation?.take_profit_pct || 0);
    
    if (!entryPrice || !tpPct) return null;
    
    if (recommendation?.action === 'BUY') {
      // Long position: TP is higher than entry
      return entryPrice * (1 + tpPct / 100);
    } else {
      // Short position: TP is lower than entry  
      return entryPrice * (1 - tpPct / 100);
    }
  };

  // Helper function to calculate stop loss price for display
  const calculateDisplayStopLossPrice = () => {
    const entryPrice = parseFloat(recommendation?.entry_price || 0);
    const slPct = parseFloat(recommendation?.stop_loss_pct || 0);
    
    if (!entryPrice || !slPct) return null;
    
    if (recommendation?.action === 'BUY') {
      // Long position: SL is lower than entry
      return entryPrice * (1 - slPct / 100);
    } else {
      // Short position: SL is higher than entry
      return entryPrice * (1 + slPct / 100);
    }
  };

  const displayTakeProfitPrice = calculateDisplayTakeProfitPrice();
  const displayStopLossPrice = calculateDisplayStopLossPrice();

  return (
    <div key={recommendation.id} className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">

    {/* ── Header ── */}
    <div className="px-4 pt-4 pb-3 sm:px-5 sm:pt-5 sm:pb-4 border-b border-slate-700/50">

      {/* Row 1 — Identity: badge + symbol + confidence | execute CTA */}
      <div className="flex items-center justify-between gap-3 mb-3">

        {/* Left: action badge + pair symbol + confidence */}
        <div className="flex items-center gap-3 min-w-0">
          <span className={`shrink-0 px-2.5 py-1 text-xs font-bold tracking-widest uppercase rounded border ${getActionColor(recommendation.action)}`}>
            {recommendation.action}
          </span>
          <h3 className="text-xl sm:text-2xl font-bold text-white leading-none truncate">
            {recommendation.pair}
          </h3>
          {recommendation.confidence && (
            <span className={`shrink-0 text-sm font-semibold tabular-nums ${getConfidenceColor(recommendation.confidence)}`}>
              {Math.round(recommendation.confidence * 100)}%
            </span>
          )}
        </div>

        {/* Right: execute button */}
        {recommendation.action !== 'HOLD' && (
          <button
            onClick={() => handleExecuteTradeClick(recommendation)}
            disabled={isExecuting}
            className={`shrink-0 px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
              isExecuting
                ? 'bg-gray-600/20 text-gray-400 cursor-not-allowed'
                : 'bg-green-700/25 hover:bg-green-600/30 text-green-400 hover:text-green-300 border border-green-700/40 hover:border-green-600/60'
            }`}
          >
            {isExecuting ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Executing…
              </>
            ) : (
              'Execute Trade'
            )}
          </button>
        )}
      </div>

      {/* Row 2 — Metrics + utility icons */}
      <div className="flex items-end justify-between gap-4">

        {/* Metrics */}
        <div className="flex items-end gap-5 sm:gap-7 flex-wrap">
          {recommendation.action !== 'HOLD' && (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Size</div>
                <div className={`text-base font-semibold tabular-nums ${
                  recommendation.recommended_position_size_usd != null && recommendation.recommended_position_size_usd > 0
                    ? 'text-white'
                    : 'text-red-400'
                }`}>
                  {recommendation.recommended_position_size_usd != null && recommendation.recommended_position_size_usd > 0
                    ? formatCurrency(recommendation.recommended_position_size_usd)
                    : <span title="Position size not calculated">⚠ N/A</span>
                  }
                </div>
              </div>

              {recommendation.recommended_position_size_usd && recommendation.leverage_amount && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Margin</div>
                  <div className="text-base font-semibold tabular-nums text-orange-400">
                    {(() => {
                      const positionSize = parseFloat(recommendation.recommended_position_size_usd);
                      const leverage = parseFloat(recommendation.leverage_amount);
                      const expectedMargin = leverage > 1 ? positionSize / leverage : positionSize;
                      return formatCurrency(expectedMargin);
                    })()}
                  </div>
                </div>
              )}
            </>
          )}

          {recommendation.created_at && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Age</div>
              <div className="text-base font-medium text-slate-300 tabular-nums">
                {formatTimeAgo(recommendation.created_at)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 hidden sm:block">
                {parseUtcDate(recommendation.created_at)?.toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {/* Utility icon buttons */}
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={handleCopyMarkdown}
            className={`p-2 rounded-lg transition-colors ${
              copied
                ? 'text-green-400 bg-green-900/30'
                : 'text-slate-400 hover:text-cyan-300 hover:bg-cyan-600/15'
            }`}
            title="Copy as Markdown"
          >
            {copied ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            )}
          </button>

          {(recommendation.action === 'BUY' || recommendation.action === 'SHORT') && (
            <button
              onClick={() => onEdit(recommendation.id)}
              disabled={editingId === recommendation.id}
              className="p-2 rounded-lg text-slate-400 hover:text-blue-300 hover:bg-blue-600/15 transition-colors disabled:opacity-40"
              title="Edit recommendation"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}

          <button
            onClick={() => {
              showWarning(
                'Delete Recommendation',
                'Are you sure you want to delete this recommendation? This action cannot be undone.',
                {
                  showCancel: true,
                  confirmText: 'Delete',
                  cancelText: 'Cancel',
                  onConfirm: () => deleteRecommendation(recommendation.id)
                }
              );
            }}
            className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-600/15 transition-colors"
            title="Delete recommendation"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    {/* Main Content */}
    <div className="p-2 sm:p-4">
      {/* Key Metrics Grid - Only show for non-HOLD recommendations */}
      {recommendation.action !== 'HOLD' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="bg-slate-700/30 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-slate-400 mb-0.5 sm:mb-1">Leverage</div>
              <div className="text-sm font-medium text-white">
                {recommendation.leverage_amount}x
              </div>
            </div>

            <div className="bg-slate-700/30 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-slate-400 mb-0.5 sm:mb-1">Entry</div>
              <div className="text-sm font-medium text-white">
                {formatCurrency(recommendation.entry_price)}
              </div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-slate-400 mb-0.5 sm:mb-1">Take Profit</div>
              <div className="text-sm font-medium text-green-400">
                <div className="truncate">
                  {displayTakeProfitPrice ?
                    formatCurrency(displayTakeProfitPrice) :
                    (recommendation.take_profit_price && formatCurrency(recommendation.take_profit_price))
                  }
                </div>
                <div className="text-xs text-slate-500">
                  ({recommendation.take_profit_pct && formatPercentUnsigned(recommendation.take_profit_pct)})
                </div>
              </div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-slate-400 mb-0.5 sm:mb-1">Stop Loss</div>
              <div className="text-sm font-medium text-red-400">
                <div className="truncate">
                  {displayStopLossPrice ?
                    formatCurrency(displayStopLossPrice) :
                    (recommendation.stop_loss_price && formatCurrency(recommendation.stop_loss_price))
                  }
                </div>
                <div className="text-xs text-slate-500">
                  ({recommendation.stop_loss_pct && formatPercentUnsigned(recommendation.stop_loss_pct)})
                </div>
              </div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-slate-400 mb-0.5 sm:mb-1">R/R</div>
              <div className="text-sm font-medium text-white">
                {recommendation.risk_reward_ratio ? `${recommendation.risk_reward_ratio.toFixed(1)}:1` : 'N/A'}
              </div>
            </div>
          </div>

          {/* Research Buttons */}
          <div className="mb-3 sm:mb-4 flex flex-wrap gap-2">
            <TradingViewResearch 
              pair={recommendation.pair} 
              portfolioType={portfolio?.portfolio_type}
              isTestnet={portfolio?.is_testnet}
            />
          </div>
        </>
      )}

      {/* Reasoning */}
      {recommendation.reasoning && (
        <div className="mb-3 sm:mb-4">
          <h4 className="text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-2">Analysis</h4>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed bg-slate-700/20 rounded-lg p-2 sm:p-3">
            {recommendation.reasoning}
          </p>
        </div>
      )}

      {/* Market Context */}
      {recommendation.market_context && (
        <div className="mb-3 sm:mb-4">
          <h4 className="text-xs sm:text-sm font-medium text-slate-300 mb-1 sm:mb-2">Market Context</h4>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed bg-slate-700/20 rounded-lg p-2 sm:p-3">
            {recommendation.market_context}
          </p>
        </div>
      )}

      {/* Technical and Risk Factors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Technical Factors */}
        {recommendation.technical_factors && recommendation.technical_factors.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-green-300 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              Technical Factors
            </h4>
            <ul className="space-y-1">
              {recommendation.technical_factors.map((factor, index) => (
                <li key={index} className="text-xs text-slate-400 flex items-start">
                  <span className="text-green-400 mr-2 mt-1">•</span>
                  <span className="leading-relaxed">{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk Factors */}
        {recommendation.risk_factors && recommendation.risk_factors.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-red-300 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Risk Factors
            </h4>
            <ul className="space-y-1">
              {recommendation.risk_factors.map((factor, index) => (
                <li key={index} className="text-xs text-slate-400 flex items-start">
                  <span className="text-red-400 mr-2 mt-1">•</span>
                  <span className="leading-relaxed">{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Exit Reason (if applicable) */}
      {recommendation.exit_reason && (
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <h4 className="text-sm font-medium text-orange-300 mb-2">Exit Reason</h4>
          <p className="text-sm text-slate-400">{recommendation.exit_reason}</p>
        </div>
      )}
    </div>
  </div>
  );
};

export default function TradingRecommendations({
  portfolioId = 2,
  config = {
    apiBaseUrl: "http://localhost:8000"
  },
  showEmpty = true,
  portfolio = null,
  onTradeExecuted = null // Callback to refresh parent component
}) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [executingIds, setExecutingIds] = useState(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [agentKeyModalOpen, setAgentKeyModalOpen] = useState(false);
  const [showBuilderApproval, setShowBuilderApproval] = useState(false);
  const [pendingRecommendation, setPendingRecommendation] = useState(null);
  const { showSuccess, showError, showWarning } = useModalAlert();
  const { checkBeforeTrade } = useTradeGuard();

  const fetchRecommendations = async () => {
    try {
      setLoading(true);

      const agentPrivateKey = getAgentPrivateKey(portfolioId);
      const url = `${config.apiBaseUrl}/portfolio/${portfolioId}/recommendation`;
      const headers = { 'accept': 'application/json' };
      if (agentPrivateKey) {
        headers['X-Agent-Private-Key'] = agentPrivateKey;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        if (response.status === 401 && body.includes('X-Agent-Private-Key header is required') && portfolio?.portfolio_type !== 'HL_PAPER') {
          setAgentKeyModalOpen(true);
          setError('Agent key required. Please provide your agent private key.');
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setRecommendations(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      setError(err.message);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteRecommendation = async (recId) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/recommendation?rec_id=${recId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Remove the deleted recommendation from the local state
        setRecommendations(prev => prev.filter(rec => rec.id !== recId));
        console.log(result.message);
      } else {
        throw new Error(result.message || 'Failed to delete recommendation');
      }
    } catch (err) {
      console.error('Failed to delete recommendation:', err);
      // You could add a toast notification here or set an error state
      showError(
        "Delete Failed",
        `Failed to delete recommendation: ${err.message}`,
        { autoClose: true }
      );
    }
  };

  const updateRecommendation = async (recId, updateData) => {
    try {
      setIsUpdating(true);
      
      const response = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/recommendation/${recId}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Update the recommendation in local state
        setRecommendations(prev => 
          prev.map(rec => 
            rec.id === recId ? { ...rec, ...result.recommendation } : rec
          )
        );
        
        showSuccess(
          "Recommendation Updated",
          "Your recommendation has been updated successfully!",
          { autoClose: true }
        );
        
        setEditingId(null);
      } else {
        throw new Error(result.message || 'Failed to update recommendation');
      }
    } catch (err) {
      console.error('Failed to update recommendation:', err);
      showError(
        "Update Failed",
        `Failed to update recommendation: ${err.message}`,
        { autoClose: true }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = (recId) => {
    setEditingId(recId);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = (updateData) => {
    if (editingId) {
      // Ensure entry_price is preserved in the update
      const updateDataWithEntryPrice = {
        ...updateData,
        entry_price: updateData.entry_price // Preserve the entry price
      };
      updateRecommendation(editingId, updateDataWithEntryPrice);
    }
  };

  const executeRecommendation = async (recId) => {
    try {
      // Add to executing set to show spinner
      setExecutingIds(prev => new Set([...prev, recId]));
      
      const isPaper = portfolio?.portfolio_type === 'HL_PAPER';
      const agentPrivateKey = getAgentPrivateKey(portfolioId);

      if (!agentPrivateKey && !isPaper) {
        setAgentKeyModalOpen(true);
        setExecutingIds(prev => { const s = new Set(prev); s.delete(recId); return s; });
        return;
      }

      const requestBody = {
        recommendation_id: recId,
        execute_onchain: false
      };

      const execHeaders = {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      };
      if (agentPrivateKey) {
        execHeaders['X-Agent-Private-Key'] = agentPrivateKey;
      }

      const response = await fetch(`${config.apiBaseUrl}/portfolio/${portfolioId}/execute`, {
        method: 'POST',
        headers: execHeaders,
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      // Remove from executing set after response
      setExecutingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(recId);
        return newSet;
      });

      if (result.success) {
        if (!isPaper) {
          try {
            const firstTradeKey = 'katbot_first_trade_fired';
            if (!window.localStorage.getItem(firstTradeKey)) {
              const executedRec = recommendations.find(r => r.id === recId);
              const direction = executedRec?.action === 'BUY' ? 'LONG' : (executedRec?.action || '');
              trackEvent('first_trade', {
                symbol: executedRec?.pair || '',
                direction,
                portfolio_id: portfolioId,
              });
              window.localStorage.setItem(firstTradeKey, '1');
            }
          } catch (e) {
            console.warn('[GA] first_trade event failed:', e);
          }
        }
        // Remove the executed recommendation from the local state
        setRecommendations(prev => prev.filter(rec => rec.id !== recId));
        console.log(`Trade executed successfully: ${result.message}`);
        console.log('Full execution result:', result); // Add detailed logging

        // Log specific details about orders created
        if (result.orders_created) {
          console.log('Orders created during execution:', result.orders_created);
        }
        if (result.market_order) {
          console.log('Market order:', result.market_order);
        }
        if (result.stop_loss_order) {
          console.log('Stop loss order:', result.stop_loss_order);
        }
        if (result.take_profit_order) {
          console.log('Take profit order:', result.take_profit_order);
        }

        // Show refreshing state for the entire panel
        setRefreshing(true);

        // You could add a success toast notification here
        showSuccess(
          "Trade Executed Successfully",
          `Your position should now be visible and tracked on the portfolio performance tab!\n${result.message}`,
          { autoClose: true, autoCloseDelay: 5000 }
        );

        if (onTradeExecuted) {
          onTradeExecuted(result);
        }
        setRefreshing(false);
      } else {
        // Enhanced error extraction for nested error details
        let errorMessage = '';
        // Try to extract from trade_result if present in message
        try {
          // Try to parse result.message as JSON if possible
          let tradeResultObj = null;
          if (typeof result.message === 'string' && result.message.includes('trade_result')) {
            // Try to extract the JSON object from the string
            const match = result.message.match(/trade_result['"]?\s*:\s*({.*?})[,}]/s);
            if (match && match[1]) {
              try {
                tradeResultObj = JSON.parse(match[1].replace(/'/g, '"'));
              } catch (jsonErr) {
                // fallback: try eval (unsafe, but for trusted backend)
                try {
                  // eslint-disable-next-line no-eval
                  tradeResultObj = eval('(' + match[1] + ')');
                } catch (evalErr) {}
              }
            }
          }
          // If not found, try to parse the whole message as JSON
          if (!tradeResultObj && typeof result.message === 'string' && result.message.trim().startsWith('{')) {
            try {
              tradeResultObj = JSON.parse(result.message.replace(/'/g, '"'));
            } catch {}
          }
          // If we have a trade_result object, extract error/hint
          if (tradeResultObj && tradeResultObj.trade_result) {
            const tr = tradeResultObj.trade_result;
            if (tr.message) errorMessage += tr.message + '\n';
            if (tr.hint) errorMessage += tr.hint + '\n';
          } else if (typeof result.message === 'string') {
            // Fallback: try to extract the error message from the string
            const msgMatch = result.message.match(/Market order failed: ([^']+)/);
            if (msgMatch && msgMatch[1]) {
              errorMessage += 'Market order failed: ' + msgMatch[1] + '\n';
            }
            // Try to extract hint
            const hintMatch = result.message.match(/'hint':\s*'([^']+)'/);
            if (hintMatch && hintMatch[1]) {
              errorMessage += hintMatch[1] + '\n';
            }
          }
        } catch (err) {
          // fallback to generic error
        }
        // Fallback to error/message fields if nothing extracted
        if (!errorMessage) {
          errorMessage = result.error || result.message || result.detail || 'Failed to execute recommendation';
        }
        showError(
          'Trade Execution Failed',
          errorMessage,
          { autoClose: false, showCancel: false, confirmText: 'OK' }
        );
      }
    } catch (err) {
      console.error('Failed to execute recommendation:', err);
      // Remove from executing set on error
      setExecutingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(recId);
        return newSet;
      });
      // You could add a toast notification here or set an error state
      showError(
        "Trade Execution Failed",
        `Failed to execute trade: ${err.message}`,
        { autoClose: false, showCancel: false, confirmText: 'OK' }
      );
    }
  };

  const handleExecuteTradeClick = async (recommendation) => {
    // First check: Validate that position size exists
    if (!recommendation.recommended_position_size_usd) {
      showWarning(
        'Cannot Execute Trade',
        'This recommendation is missing position size information. The recommendation may be incomplete or corrupted.\\n\\nPlease delete this recommendation and create a new one.',
        {
          showCancel: false,
          confirmText: 'OK',
          cancelText: 'Cancel'
        }
      );
      return;
    }

    // Calculate expected margin requirement
    const positionSize = parseFloat(recommendation.recommended_position_size_usd);
    const leverage = parseFloat(recommendation.leverage_amount) || 1;
    const expectedMargin = leverage > 1 ? positionSize / leverage : positionSize;
    
    // Second check: Margin requirement before showing confirmation
    console.log('Portfolio data for margin check:', portfolio);
    console.log('Checking margin requirement:', { 
      expectedMargin, 
      availableBalance: portfolio?.available_balance_usd,
      portfolioKeys: portfolio ? Object.keys(portfolio) : 'null'
    });

    if (!portfolio) {
      showError(
        'Portfolio Data Missing',
        `Cannot execute trade because portfolio information is not loaded. Please refresh the page and try again.`,
      );
      return;
    }

    // Only enforce the margin cap when balance data is actually available
    if (portfolio.available_balance_usd !== null && portfolio.available_balance_usd !== undefined) {
      const availableBalance = parseFloat(portfolio.available_balance_usd);
      if (!isNaN(availableBalance) && availableBalance > 0) {
        const marginRatio = expectedMargin / availableBalance;
        if (marginRatio > 0.9) {
          showError(
            'Margin Requirement Too High',
            `This trade requires ${formatCurrency(expectedMargin)} in margin, which is ${(marginRatio * 100).toFixed(1)}% of your available balance (${formatCurrency(availableBalance)}).\n\nFor safety, we don't allow trades that require more than 90% of your available balance. Please reduce the position size or add more funds to your account.`,
          );
          return;
        }
      }
    }

    // For live Hyperliquid portfolios, require builder fee approval before trading
    if (portfolio?.portfolio_type === 'HYPERLIQUID') {
      const approved = await checkBeforeTrade(portfolio);
      if (!approved) {
        setPendingRecommendation(recommendation);
        setShowBuilderApproval(true);
        return;
      }
    }

    // All checks passed, show confirmation dialog
    const positionSizeFormatted = formatCurrency(recommendation.recommended_position_size_usd);
    const confidenceText = recommendation.confidence 
      ? `${Math.round(recommendation.confidence * 100)}% confidence`
      : 'N/A';
    
    showWarning(
      'Execute Trade',
      `Are you sure you want to execute this trade?\n\nTrade Details:\n• Action: ${recommendation.action.toUpperCase()}\n• Pair: ${recommendation.pair}\n• Position Size: ${positionSizeFormatted}\n• Expected Margin: ${formatCurrency(expectedMargin)}\n• Confidence: ${confidenceText}`,
      {
        showCancel: true,
        confirmText: 'Execute Trade',
        cancelText: 'Cancel',
        onConfirm: () => executeRecommendation(recommendation.id)
      }
    );
  };

  useEffect(() => {
    // Initial fetch
    fetchRecommendations();
  }, [portfolioId]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = parseFloat(value);
    const abs = Math.abs(num);

    let fractionDigits;
    if (abs === 0 || abs >= 1)    fractionDigits = 2;  // $1.00, $90,000
    else if (abs >= 0.1)          fractionDigits = 4;  // $0.1234  (ARB ~$0.20)
    else if (abs >= 0.01)         fractionDigits = 5;  // $0.03471 (MON ~$0.035)
    else if (abs >= 0.001)        fractionDigits = 6;  // $0.003471
    else                          fractionDigits = 8;  // sub-penny tokens

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = parseFloat(value);
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  const formatPercentUnsigned = (value) => {
    const num = parseFloat(value);
    return `${num >= 0 ? '' : ''}${num.toFixed(2)}%`;
  };


  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'buy':
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'sell':
        return 'text-red-400 bg-red-400/10 border-red-400/20';
      case 'short':
        return 'text-orange-400 bg-orange-400/10 border-orange-400/20';
      case 'cover':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
      case 'hold':
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-400';
    if (confidence >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading && recommendations.length === 0) {
    return (
      <div className="w-full p-2">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-white">Loading recommendations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-2">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-red-400 font-semibold mb-1">Failed to load recommendations</h3>
              <p className="text-red-300 text-sm">{error}</p>
              <button
                onClick={fetchRecommendations}
                className="mt-3 px-4 py-2 bg-red-800 hover:bg-red-700 text-white rounded text-sm transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0 && !showEmpty) {
    return null;
  }

  return (
    <div className="w-full p-2 space-y-2 relative">
      {/* Agent identity banner */}
      {portfolio?.active_agent && (
        <AgentIdentityCard agent={portfolio.active_agent} label="Recommendations from" />
      )}
      {/* Header */}
      <div className="flex flex-col sm:items-center sm:justify-between gap-2 mt-2">
        {/* <h2 className="text-2xl font-bold text-white m-0">Trading Recommendations</h2> */}
        <div className="flex flex-col w-full items-center gap-3 sm:flex-row sm:justify-between">
        <AgentRecommendation 
          config={config}
          portfolio={portfolio}
          portfolioId={portfolioId}
          onRecommendationGenerated={() => {
            // Refresh the recommendations list
            fetchRecommendations();
          }}
        />
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchRecommendations}
            disabled={loading}
            className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            title="Refresh recommendations"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {lastUpdated && (
            <span className="text-sm text-slate-400">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Empty State */}
      {recommendations.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mt-0 text-center">
          <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No recommendations available</h3>
          <p className="text-slate-400">
            Trading recommendations will appear here when generated by your trading agent.
          </p>
        </div>
      ) : (
        /* Recommendations List */
        <div className="space-y-4">
          {recommendations.map((rec) => (
            editingId === rec.id ? (
              <TradingRecommendationForm
                key={`edit-${rec.id}`}
                recommendation={rec}
                onSubmit={handleSaveEdit}
                onCancel={handleCancelEdit}
                isSubmitting={isUpdating}
              />
            ) : (
              <TradingRecommendationItem
                key={rec.id}
                recommendation={rec}
                getActionColor={getActionColor}
                getConfidenceColor={getConfidenceColor}
                deleteRecommendation={deleteRecommendation}
                handleExecuteTradeClick={handleExecuteTradeClick}
                isExecuting={executingIds.has(rec.id)}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
                formatPercentUnsigned={formatPercentUnsigned}
                showWarning={showWarning}
                onEdit={handleEdit}
                editingId={editingId}
                portfolio={portfolio}
              />
            )
          ))}
        </div>
      )}

      {/* Refreshing Overlay */}
      {refreshing && (
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-50">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-16 w-16 text-cyan-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h3 className="text-white text-lg font-medium">Refreshing Portfolio...</h3>
            <p className="text-slate-400 text-sm mt-2">Please wait while we update your positions</p>
          </div>
        </div>
      )}

      <AgentKeyModal
        isOpen={agentKeyModalOpen}
        portfolioId={portfolioId}
        isTestnet={portfolio?.is_testnet}
        portfolioName={portfolio?.name}
        onSave={() => {
          setAgentKeyModalOpen(false);
          setError(null);
          fetchRecommendations();
        }}
        onClose={() => setAgentKeyModalOpen(false)}
      />

      <BuilderFeeApprovalModal
        isOpen={showBuilderApproval}
        portfolioId={portfolioId}
        portfolio={portfolio}
        config={config}
        onClose={() => {
          setShowBuilderApproval(false);
          setPendingRecommendation(null);
        }}
        onApproved={() => {
          setShowBuilderApproval(false);
          if (pendingRecommendation) {
            const rec = pendingRecommendation;
            setPendingRecommendation(null);
            handleExecuteTradeClick(rec);
          }
        }}
      />
    </div>
  );
}
